import { useMemo, useCallback, ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { IWidgetBase, TimeSeriesDataPoint, ILineChartConfig } from '../../../features/metrics/Metrics.model';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import WidgetContainer from '../../../components/Atoms/WidgetContainer/WidgetContainer';
import ChartEmptyState from '../../../components/Atoms/ChartEmptyState';

export interface ILineChartWidgetProps extends IWidgetBase {
  unit?: string;
  data?: TimeSeriesDataPoint[];
  config?: ILineChartConfig;
  emptyStateMessage?: string;
  header?: ReactNode;
}

const DEFAULT_CONFIG: ILineChartConfig = {
  yAxisLabel: 'Value',
  yAxisDomain: [0, 'auto'],
  tooltipLabel: 'Value',
};

const LineChartWidget = (props: ILineChartWidgetProps) => {
  const theme = useTheme();
  const { unit, config = {}, data = [], header } = props;

  const chartConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
    }),
    [config]
  );

  const formatValue = useCallback(
    (value: number) => {
      return unit ? `${value}${unit}` : `${value}`;
    },
    [unit]
  );

  const hasData = data.length > 0;

  return (
    <WidgetContainer
      title={props.title}
      infoMessage={props.infoMessage}
      isLoading={props.isLoading}
      error={props.error}
      minHeight={400}
      maxHeight={650}
    >
      {header && <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>{header}</Box>}
      {!hasData && !props.isLoading && (
        <ChartEmptyState message={props.emptyStateMessage || chartConfig.emptyStateMessage} />
      )}
      {hasData && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            '& .recharts-wrapper *:focus:not(:focus-visible)': {
              outline: 'none !important',
            },
          }}
        >
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray='3 3' stroke={theme.palette.divider} />
              <XAxis
                dataKey='label'
                tick={{
                  fontSize: 12,
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.lightGrey.main,
                  style: { userSelect: 'none' },
                }}
                tickLine={{ stroke: theme.palette.lightGrey.main }}
                axisLine={{ stroke: theme.palette.lightGrey.main }}
                label={{
                  value: chartConfig.xAxisLabel,
                  position: 'bottom',
                  fill: theme.palette.lightGrey.main,
                  fontSize: 14,
                  fontWeight: 600,
                  style: { fontFamily: theme.typography.fontFamily, userSelect: 'none' },
                }}
              />
              <YAxis
                domain={chartConfig.yAxisDomain}
                tick={{
                  fontSize: 12,
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.lightGrey.main,
                  style: { userSelect: 'none' },
                }}
                tickLine={{ stroke: theme.palette.lightGrey.main }}
                axisLine={{ stroke: theme.palette.lightGrey.main }}
                tickFormatter={formatValue}
                label={{
                  value: chartConfig.yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  fill: theme.palette.lightGrey.main,
                  fontSize: 14,
                  fontWeight: 600,
                  style: {
                    textAnchor: 'middle',
                    fontFamily: theme.typography.fontFamily,
                    userSelect: 'none',
                  },
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.common.white,
                  borderRadius: 4,
                  boxShadow: '2px 2px 6px -4px #999',
                  fontFamily: theme.typography.fontFamily,
                }}
                labelStyle={{
                  color: theme.palette.lightGrey.main,
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: theme.typography.fontFamily,
                }}
                formatter={value => [formatValue(value as number), chartConfig.tooltipLabel]}
              />
              <Line
                type='monotone'
                dataKey='value'
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{
                  fill: theme.palette.common.white,
                  stroke: theme.palette.primary.main,
                  strokeWidth: 2,
                  r: 4,
                }}
                activeDot={{
                  fill: theme.palette.primary.main,
                  stroke: theme.palette.common.white,
                  strokeWidth: 2,
                  r: 6,
                }}
                isAnimationActive={props.animated}
                animationDuration={1500}
                animationEasing='ease-in-out'
                animationBegin={0}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </WidgetContainer>
  );
};

export default LineChartWidget;
